import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-3 shrink-0">
      <img
        src="https://storage.readdy-site.link/project_files/3ad77496-7e33-4bee-aed3-853667869f76/3a6b7789-d222-4345-bb6d-4232140708c8_TIE1.png?v=90453bddcbe5657cab713355118e545f"
        alt="TIE - Textile Innovation Exchange"
        className="h-12 md:h-20 w-auto object-contain drop-shadow-sm"
      />
    </Link>
  );
}

interface MenuItem {
  label: string;
  href: string;
  children?: {
    label: string;
    href: string;
    children?: { label: string; href: string }[];
  }[];
}

const menuItems: MenuItem[] = [
  { label: "Home", href: "/" },
  {
    label: "Programs",
    href: "/programs",
    children: [
      {
        label: "Innovation Wing",
        href: "/wings/innovation",
        children: [
          { label: "PIIC", href: "/piic" },
          { label: "PTIM", href: "/ptim" },
          { label: "Training", href: "/training" },
          { label: "Experts", href: "/experts" },
        ],
      },
      {
        label: "Research Wing",
        href: "/wings/research",
        children: [
          { label: "PIARC", href: "/piarc" },
          { label: "IJTAI", href: "/ijtai" },
          { label: "AIRCon", href: "/aircon" },
        ],
      },
      {
        label: "Readiness & Recognition",
        href: "/wings/readiness",
        children: [
          {
            label: "Innovation Readiness Certification",
            href: "/wings/readiness",
          },
          { label: "Textile Innovation Awards", href: "/wings/readiness" },
        ],
      },
      {
        label: "Expo Wing",
        href: "/wings/expo",
        children: [
          {
            label: "Textile Innovation Expo",
            href: "https://www.textileinnovationexpo.com/",
          },
          { label: "Concurrent Expos", href: "/wings/expo" },
        ],
      },
    ],
  },
  { label: "PIIC", href: "/piic" },
  { label: "PTIM", href: "/ptim" },
  { label: "Training", href: "/training" },
   { label: "Media", href: "/media" },
  { label: "Press Release", href: "/press-release" },
  { label: "Tech Partners", href: "/technology-partners" },
  { label: "Ecosystem", href: "/ecosystem" },
  { label: "Expo", href: "https://www.textileinnovationexpo.com/" },
];

function isExternalLink(href: string) {
  return href.startsWith("http://") || href.startsWith("https://");
}

function ProgramsMegaMenu({
  scrolled,
  isHome,
}: {
  scrolled: boolean;
  isHome: boolean;
}) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpen(true);
  };

  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  const baseBtnClass = `inline-flex items-center justify-center px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
    scrolled || !isHome
      ? "bg-tie-navy/10 text-tie-navy hover:bg-tie-navy hover:text-white"
      : "bg-white/15 text-tie-navy hover:bg-white/30 hover:text-white border border-white/20"
  }`;

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <Link to="/programs" className={baseBtnClass}>
        Programs
        <i
          className={`ri-arrow-down-s-line ml-1 text-xs transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </Link>

      {open && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[720px] bg-white rounded-2xl shadow-xl border border-tie-grey-dark overflow-hidden z-50">
          <div className="grid grid-cols-2 gap-0">
            {menuItems[1].children?.map((wing) => (
              <div
                key={wing.label}
                className="p-4 border-b border-r border-tie-grey-dark/50"
              >
                <Link
                  to={wing.href}
                  className="block text-sm font-bold text-tie-text-primary hover:text-tie-teal transition-colors mb-2"
                >
                  {wing.label}
                </Link>
                <ul className="space-y-1">
                  {wing.children?.map((prog) => {
                    const external = isExternalLink(prog.href);
                    return external ? (
                      <li key={prog.label}>
                        <a
                          href={prog.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-tie-text-secondary hover:text-tie-teal transition-colors inline-flex items-center gap-1"
                        >
                          {prog.label}
                          <i className="ri-external-link-line text-[10px]" />
                        </a>
                      </li>
                    ) : (
                      <li key={prog.label}>
                        <Link
                          to={prog.href}
                          className="text-xs text-tie-text-secondary hover:text-tie-teal transition-colors"
                        >
                          {prog.label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
          <div className="px-4 py-2.5 bg-tie-grey-light border-t border-tie-grey-dark">
            <Link
              to="/programs"
              className="text-xs font-semibold text-tie-teal hover:text-tie-teal-dark transition-colors inline-flex items-center gap-1"
            >
              View All Programmes
              <i className="ri-arrow-right-line text-xs" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileProgramsOpen, setMobileProgramsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMobileProgramsOpen(false);
  }, [location]);

  const isHome = location.pathname === "/";

  const handleQuickNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    if (isExternalLink(href)) return;
    if (href.startsWith("#")) {
      if (isHome) {
        e.preventDefault();
        const target = document.querySelector(href);
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }
  };

  const baseLinkClass = (active = false) =>
    `inline-flex items-center justify-center px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
      scrolled || !isHome
        ? active
          ? "text-tie-navy"
          : "bg-tie-navy/10 text-tie-navy hover:bg-tie-navy hover:text-white"
        : active
          ? "bg-white/30 text-tie-navy border border-white/30"
          : "bg-white/15 text-tie-navy hover:bg-white/30 hover:text-white border border-white/20"
    }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || !isHome
          ? "bg-white/95 backdrop-blur-md border-b border-tie-grey-dark"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Logo />

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-2 xl:gap-3">
            {menuItems.map((item) => {
              if (item.label === "Programs") {
                return (
                  <ProgramsMegaMenu
                    key={item.label}
                    scrolled={scrolled}
                    isHome={isHome}
                  />
                );
              }
              const external = isExternalLink(item.href);
              const active = location.pathname === item.href;
              return external ? (
                <a
                  key={item.label}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={baseLinkClass(active)}
                >
                  {item.label}
                  <i className="ri-external-link-line ml-1.5 text-xs" />
                </a>
              ) : (
                <Link
                  key={item.label}
                  to={
                    item.href.startsWith("#") && !isHome
                      ? `/${item.href}`
                      : item.href
                  }
                  onClick={(e) => handleQuickNavClick(e, item.href)}
                  className={baseLinkClass(active)}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link
              to="/collaborate"
              className={`inline-flex items-center px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                scrolled || !isHome
                  ? "bg-[#0A1F44] text-white border border-[#0A1F44] hover:bg-[#142B5C]"
                  : "bg-[#0A1F44]/80 text-white border border-white/30 hover:bg-[#0A1F44]"
              }`}
            >
              Collaborate
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden p-2 rounded-md transition-colors ${
              scrolled || !isHome ? "text-tie-navy" : "text-white"
            }`}
            aria-label="Toggle menu"
          >
            <i
              className={`ri-${mobileOpen ? "close" : "menu"}-line text-2xl`}
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden absolute top-full left-0 right-0 bg-white border-b border-tie-grey-dark transition-all duration-300 overflow-hidden ${
          mobileOpen ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-4 py-4 space-y-1">
          {menuItems.map((item) => {
            if (item.label === "Programs") {
              return (
                <div key={item.label}>
                  <button
                    onClick={() => setMobileProgramsOpen(!mobileProgramsOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium text-tie-text-primary hover:bg-tie-grey transition-colors"
                  >
                    <span>Programs</span>
                    <i
                      className={`ri-arrow-down-s-line transition-transform ${mobileProgramsOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {mobileProgramsOpen && (
                    <div className="ml-4 mt-1 space-y-1 border-l-2 border-tie-grey-dark pl-3">
                      {item.children?.map((wing) => (
                        <div key={wing.label} className="py-1">
                          <Link
                            to={wing.href}
                            className="block px-3 py-2 rounded-md text-sm font-semibold text-tie-text-primary hover:bg-tie-grey transition-colors"
                          >
                            {wing.label}
                          </Link>
                          <div className="ml-3 space-y-0.5">
                            {wing.children?.map((prog) => {
                              const external = isExternalLink(prog.href);
                              return external ? (
                                <a
                                  key={prog.label}
                                  href={prog.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block px-3 py-1.5 rounded-md text-xs text-tie-text-secondary hover:bg-tie-grey transition-colors inline-flex items-center gap-1"
                                >
                                  {prog.label}
                                  <i className="ri-external-link-line text-[10px]" />
                                </a>
                              ) : (
                                <Link
                                  key={prog.label}
                                  to={prog.href}
                                  className="block px-3 py-1.5 rounded-md text-xs text-tie-text-secondary hover:bg-tie-grey transition-colors"
                                >
                                  {prog.label}
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            const external = isExternalLink(item.href);
            const active = location.pathname === item.href;
            const baseClasses = `block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
              active
                ? "bg-tie-teal/10 text-tie-teal"
                : "text-tie-text-primary hover:bg-tie-grey"
            }`;
            return external ? (
              <a
                key={item.label}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`${baseClasses} flex items-center justify-between`}
              >
                {item.label}
                <i className="ri-external-link-line text-xs text-tie-text-muted" />
              </a>
            ) : (
              <Link
                key={item.label}
                to={
                  item.href.startsWith("#") && !isHome
                    ? `/${item.href}`
                    : item.href
                }
                onClick={(e) => handleQuickNavClick(e, item.href)}
                className={baseClasses}
              >
                {item.label}
              </Link>
            );
          })}
          <div className="pt-2">
            <Link
              to="/collaborate"
              className="block px-4 py-3 rounded-lg bg-tie-teal text-white text-sm font-medium text-center"
            >
              Collaborate with TIE
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}
